import { ExecutionTodo, ExecutionTodoItemStatus } from '@stores/chatbot/types';

const VALID_ITEM_STATUSES: ExecutionTodoItemStatus[] = [
  'pending',
  'in_progress',
  'completed',
  'cancelled',
];
const VALID_PLAN_STATUSES = new Set(['active', 'completed', 'cancelled']);

const asString = (value: unknown, fallback = ''): string =>
  typeof value === 'string' ? value.trim() : fallback;

const asNumber = (value: unknown, fallback: number): number =>
  typeof value === 'number' && Number.isFinite(value) ? value : fallback;

const normalizeItemStatus = (value: unknown): ExecutionTodoItemStatus => {
  const raw = asString(value).toLowerCase();
  if (VALID_ITEM_STATUSES.includes(raw as ExecutionTodoItemStatus)) {
    return raw as ExecutionTodoItemStatus;
  }
  return 'pending';
};

const normalizePlanStatus = (value: unknown): ExecutionTodo['status'] => {
  const raw = asString(value).toLowerCase();
  if (VALID_PLAN_STATUSES.has(raw)) {
    return raw as ExecutionTodo['status'];
  }
  return 'active';
};

const tryParseJson = (value: unknown): unknown => {
  if (typeof value !== 'string') return value;
  const trimmed = value.trim();
  if (!trimmed.startsWith('{') || !trimmed.endsWith('}')) {
    return value;
  }
  try {
    return JSON.parse(trimmed);
  } catch {
    return value;
  }
};

export const normalizeExecutionTodo = (input: unknown): ExecutionTodo | null => {
  const parsed = tryParseJson(input);
  if (!parsed || typeof parsed !== 'object') return null;

  const maybeRecord = parsed as Record<string, unknown>;
  const maybeItems = maybeRecord.items;
  if (!Array.isArray(maybeItems)) return null;

  const normalizedItems = maybeItems
    .map((item, index) => {
      if (!item || typeof item !== 'object') return null;
      const itemRecord = item as Record<string, unknown>;
      const id = asString(itemRecord.id, `step_${index + 1}`);
      const title = asString(itemRecord.title, `Step ${index + 1}`);
      const description = asString(itemRecord.description, '');
      const status = normalizeItemStatus(itemRecord.status);
      const order = asNumber(itemRecord.order, index);
      const updatedAt = asString(itemRecord.updated_at, '');
      const note = asString(itemRecord.note, '');
      return {
        id,
        title,
        description: description || undefined,
        status,
        order,
        updated_at: updatedAt || undefined,
        note: note || undefined,
      };
    })
    .filter((item): item is NonNullable<typeof item> => Boolean(item))
    .sort((a, b) => a.order - b.order);

  if (normalizedItems.length === 0) return null;

  const title = asString(maybeRecord.title, 'Execution plan');
  const activeItemIdRaw = asString(maybeRecord.active_item_id, '');
  const activeItemId = activeItemIdRaw
    || normalizedItems.find((item) => item.status === 'in_progress')?.id
    || null;
  const updatedAt = asString(maybeRecord.updated_at, '') || new Date().toISOString();
  const createdAt = asString(maybeRecord.created_at, '') || undefined;
  const source = asString(maybeRecord.source, 'leo');
  const version = asNumber(maybeRecord.version, 1);
  const status = normalizePlanStatus(maybeRecord.status);

  const lastUpdateRaw = maybeRecord.last_update;
  let lastUpdate: ExecutionTodo['last_update'] | undefined;
  if (lastUpdateRaw && typeof lastUpdateRaw === 'object') {
    const record = lastUpdateRaw as Record<string, unknown>;
    const type = asString(record.type, '');
    const note = asString(record.note, '');
    const updated_at = asString(record.updated_at, '');
    const changes = Array.isArray(record.changes)
      ? record.changes.map((entry) => asString(entry, '')).filter(Boolean)
      : undefined;
    lastUpdate = {
      type: type || undefined,
      note: note || undefined,
      updated_at: updated_at || undefined,
      changes,
    };
  }

  return {
    title,
    version,
    status,
    source,
    created_at: createdAt,
    updated_at: updatedAt,
    active_item_id: activeItemId,
    items: normalizedItems,
    last_update: lastUpdate,
  };
};

export const extractExecutionTodoFromPayload = (payload: unknown): ExecutionTodo | null => {
  const parsed = tryParseJson(payload);
  if (!parsed || typeof parsed !== 'object') {
    return null;
  }

  const record = parsed as Record<string, unknown>;
  if (record.execution_todo) {
    return normalizeExecutionTodo(record.execution_todo);
  }
  return normalizeExecutionTodo(record);
};

export const extractExecutionTodoFromStreamChunk = (chunk: unknown): ExecutionTodo | null => {
  if (!chunk || typeof chunk !== 'object') return null;
  const record = chunk as Record<string, unknown>;

  if (record.todo_snapshot) {
    const fromSnapshot = normalizeExecutionTodo(record.todo_snapshot);
    if (fromSnapshot) return fromSnapshot;
  }

  if (record.output !== undefined) {
    const fromOutput = extractExecutionTodoFromPayload(record.output);
    if (fromOutput) return fromOutput;
  }

  if (record.message !== undefined) {
    return extractExecutionTodoFromPayload(record.message);
  }

  return null;
};

